<template>
  <app-layout :heading="$t('heading')">
    <PaymentCancelSubscription
      :user="getUser"
      v-bind="{user: getUser, resumeSubscription, cancelSubscription}"
      @subscriptionResumed="updateUser"
      @subscriptionCanceled="updateUser"
    />

    <Payment
      :stripe-public-key="stripePublicKey"
      :plans="paymentPlans"
      :get-intent="getIntent"
      :checkout="checkout"
      :address="company | mappedAddress"
      :current-plan="currentPlan"
      :lang="getUser.lang.name"
    />

  </app-layout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Validator } from 'vee-validate'

import SubscriptionApi from '@/api/SubscriptionApi'
import CompanyApi from '@/api/CompanyApi'

export default {
  name: 'Subscription',
  filters: {
    mappedAddress (value) {
      if (!value) return
      let mappedCompany = {}
      mappedCompany['company_name'] = value['name']
      mappedCompany['name'] = value['contact_person']
      mappedCompany['email'] = value['email']
      mappedCompany['address_line1'] = value['street']
      mappedCompany['address_line2'] = null
      mappedCompany['address_state'] = null
      mappedCompany['address_zip'] = value['city']['zip']
      mappedCompany['address_city'] = value['city']['name']
      mappedCompany['address_country'] = 'Germany'
      return mappedCompany
    }
  },
  data () {
    return {
      stripePublicKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY,
      isLoading: false,
      paymentPlans: [],
      currentPlan: {},
      company: null
    }
  },
  computed: {
    ...mapGetters({
      getUser: 'user/getUser',
      paymentPlansList: 'paymentPlans/paymentPlansList'
    })
  },
  methods: {
    ...mapActions('paymentPlans', ['setPaymentPlans']),
    ...mapActions('user', ['updateUser']),
    async getIntent () {
      const { intent } = await SubscriptionApi.createIntent()
      return intent
    },

    async checkout (data) {
      let { paymentDetails, plan } = data
      const payment_method = paymentDetails.token
      plan = plan.alias
      this.isLoading = true
      try {
        const result = await SubscriptionApi.subscribe({
          payment_method,
          plan
        })

        await this.updateUser()

        this.$notification(result)
        this.$router.push({ name: 'subscription' })
      } catch (e) {
        this.$notification(e)
      }
      this.isLoading = false
    },

    cancelSubscription () {
      return SubscriptionApi.cancel()
    },
    resumeSubscription () {
      return SubscriptionApi.resume()
    },

    setPlans (plans) {
      const p = [...plans]
      p.sort((a, b) => a.price - b.price)
      this.paymentPlans = p || []
    }
  },

  watch: {
    paymentPlansList: {
      handler: 'setPlans',
      immediate: true
    }
  },

  i18n: {
    messages: {
      de: {
        heading: 'Abonnement',
        manage: 'Abo verwalten',
        paymentMethod: 'Zahlungsart ändern',
        changePlan: 'Paket wechseln'
      },
      en: {
        heading: 'Subscription',
        manage: 'Manage subscription',
        paymentMethod: 'Change payment type',
        changePlan: 'Change package'
      },
      ru: {
        heading: 'Подписка',
        manage: 'Управление подпиской',
        paymentMethod: 'Изменить тип платежа',
        changePlan: 'Изменить пакет'
      },
      tr: {
        heading: 'Üyelik',
        manage: 'Üyelik yönetimi',
        paymentMethod: 'Ödeme türünü değiştir',
        changePlan: 'Paketi değiştir'
      }
    }
  },

  async created () {
    this.setPaymentPlans()

    const locale = await import(`vee-validate/dist/locale/${this.getUser.lang.name}`)

    Validator.localize(this.getUser.lang.name, locale)

    this.company = await CompanyApi.getCompany()

    if (!this.getUser.subscription_data) return
    const { plan } = this.getUser.subscription_data
    this.currentPlan = plan || {}
  }
}
</script>

<style>
.StripeElementGroup {
  position: relative;
}

.StripeElement {
  font-size: 18px;
  padding: 10px 10px 10px 5px;
  display: block;
  width: 100%;
  border-bottom: 1px solid rgba(0, 0, 0, 0.42);
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.StripeElement:hover {
  border-bottom: 1px solid rgba(0, 0, 0, 0.87);
}

.StripeElement:focus {
  outline: none;
}

.bar {
  position: relative;
  display: block;
  width: 100%;
  height: 1px;
}

.bar:before,
.bar:after {
  content: "";
  height: 2px;
  width: 0;
  bottom: 1px;
  position: absolute;
  background: rgb(25, 118, 210);
  -webkit-transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.bar:before {
  left: 50%;
}

.bar:after {
  right: 50%;
}

.StripeElement--focus ~ .bar:before,
.StripeElement--focus ~ .bar:after {
  width: 50%;
}

.highlight {
  position: absolute;
  height: 60%;
  width: 100px;
  top: 25%;
  left: 0;
  pointer-events: none;
  opacity: 0.5;
}
</style>
